/**
 * Phase 3 domain vocabulary: markets and serviceability, customer accounts,
 * favorites, lists, carts, leads, attribution and analytics. As with the Phase 2
 * vocabulary these literal unions are the single source of truth shared by the
 * API, the services and the SQL CHECK constraints, and the migration tests assert
 * the two agree.
 *
 * Nothing in this file describes a sensitive personal characteristic. There is no
 * race, ethnicity, gender, complexion or age vocabulary in Phase 3, by design:
 * the target audience is understood through behaviour the customer chose to
 * perform, never through an inference about who they are.
 */

// -- Markets and serviceability --------------------------------------------

export const MARKET_STATUSES = ['PLANNED', 'WAITLIST', 'LIVE', 'PAUSED', 'RETIRED'] as const;
export type MarketStatus = (typeof MARKET_STATUSES)[number];

export const SERVICE_AREA_STATUSES = MARKET_STATUSES;
export type ServiceAreaStatus = MarketStatus;

/**
 * Outcome of a serviceability check. WAITLIST is distinct from OUT_OF_AREA on
 * purpose: a customer in a planned market is a lead, a customer nowhere near one
 * is not, and telling them apart is the whole point of capturing the ZIP.
 */
export const SERVICEABILITY_STATES = ['UNKNOWN', 'SERVICEABLE', 'WAITLIST', 'OUT_OF_AREA'] as const;
export type ServiceabilityState = (typeof SERVICEABILITY_STATES)[number];

export const FULFILLMENT_MODES = ['UNSELECTED', 'PICKUP', 'DELIVERY', 'OVERNIGHT'] as const;
export type FulfillmentMode = (typeof FULFILLMENT_MODES)[number];

// -- Customer availability labels -------------------------------------------

/**
 * The only availability labels the customer surface may render. Each one is
 * produced exclusively by the availability composer, which requires every
 * configured condition to pass; no caller may construct one directly from a
 * retailer opt-in.
 */
export const AVAILABILITY_LABELS = [
  'OPEN_NOW',
  'OPEN_LATE',
  'AVAILABLE_OVERNIGHT',
  'TWENTY_FOUR_HOURS',
  'CLOSED',
  'OPENS_LATER',
  'UNAVAILABLE',
] as const;
export type AvailabilityLabel = (typeof AVAILABILITY_LABELS)[number];

/** Customer-readable copy for each label. Kept beside the union so the two agree. */
export const AVAILABILITY_LABEL_TEXT: Readonly<Record<AvailabilityLabel, string>> = {
  OPEN_NOW: 'Open Now',
  OPEN_LATE: 'Open Late',
  AVAILABLE_OVERNIGHT: 'Available Overnight',
  TWENTY_FOUR_HOURS: '24 Hours',
  CLOSED: 'Closed',
  OPENS_LATER: 'Opens Later',
  UNAVAILABLE: 'Unavailable',
};

/**
 * Truthful representation of delivery provider readiness. Phase 3 has no live
 * courier, so the customer surface must say so rather than implying a delivery
 * that cannot happen. NOT_CONNECTED is the honest Phase 3 value.
 */
export const PROVIDER_READINESS = [
  'NOT_CONNECTED',
  'MOCK',
  'SANDBOX',
  'LIVE',
  'UNAVAILABLE',
] as const;
export type ProviderReadiness = (typeof PROVIDER_READINESS)[number];

// -- Customer account -------------------------------------------------------

export const MEMBERSHIP_STATES = ['NONE', 'INTERESTED', 'WAITLISTED'] as const;
export type MembershipState = (typeof MEMBERSHIP_STATES)[number];

export const CONSENT_TYPES = [
  'MARKETING_EMAIL',
  'MARKETING_SMS',
  'MARKETING_PUSH',
  'TRANSACTIONAL_SMS',
  'TERMS_OF_SERVICE',
  'PRIVACY_POLICY',
  'AGE_CONFIRMATION',
  'ANALYTICS',
] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];

export const CONSENT_SOURCES = [
  'SIGNUP',
  'ACCOUNT_SETTINGS',
  'HOMEPAGE_LEAD',
  'MEMBERSHIP_LEAD',
  'AFTER_HOURS_LEAD',
  'WAITLIST_LEAD',
  'RETAILER_LEAD',
  'CHECKOUT',
  'ADMIN',
  'IMPORT',
] as const;
export type ConsentSource = (typeof CONSENT_SOURCES)[number];

/** Account hub sections. Sections owned by a later phase render as unavailable. */
export const ACCOUNT_SECTIONS = [
  'PROFILE',
  'ORDERS',
  'FAVORITES',
  'LISTS',
  'MEMBERSHIP',
  'ADDRESSES',
  'PAYMENT_METHODS',
  'NOTIFICATIONS',
  'REVIEWS',
  'REFUNDS',
  'PRIVACY',
] as const;
export type AccountSection = (typeof ACCOUNT_SECTIONS)[number];

// -- Cart -------------------------------------------------------------------

export const CART_STATUSES = ['ACTIVE', 'MERGED', 'ABANDONED', 'CONVERTED', 'EXPIRED'] as const;
export type CartStatus = (typeof CART_STATUSES)[number];

/**
 * Per-line result of the most recent revalidation. PRICE_CHANGED is a first-class
 * state rather than a silent correction: the customer is told the price moved.
 */
export const CART_ITEM_AVAILABILITY = [
  'UNKNOWN',
  'AVAILABLE',
  'PARTIAL',
  'OUT_OF_STOCK',
  'LISTING_UNAVAILABLE',
  'PRICE_CHANGED',
  'NOT_SERVICEABLE',
] as const;
export type CartItemAvailability = (typeof CART_ITEM_AVAILABILITY)[number];

// -- Leads ------------------------------------------------------------------

/** Configurable lead funnel statuses. Deliberately small; this is not a CRM. */
export const LEAD_STATUSES = [
  'NEW',
  'CONTACTED',
  'QUALIFIED',
  'CONVERTED',
  'NURTURE',
  'CLOSED',
] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

export const LEAD_KINDS = ['CUSTOMER', 'RETAILER', 'MEMBERSHIP'] as const;
export type LeadKind = (typeof LEAD_KINDS)[number];

export const CUSTOMER_LEAD_INTERESTS = [
  'GENERAL',
  'MEMBERSHIP',
  'AFTER_HOURS',
  'SERVICE_AREA_WAITLIST',
  'BACK_IN_STOCK',
  'PROMOTION',
  'BEAUTY_PROFESSIONAL',
] as const;
export type CustomerLeadInterest = (typeof CUSTOMER_LEAD_INTERESTS)[number];

export const CUSTOMER_LEAD_SOURCES = [
  'HOMEPAGE',
  'MEMBERSHIP_PAGE',
  'AFTER_HOURS_PAGE',
  'WAITLIST',
  'SIGNUP_FUNNEL',
  'LANDING_PAGE',
  'PRODUCT_PAGE',
  'STORE_PAGE',
  'ADMIN',
  'OTHER',
] as const;
export type CustomerLeadSource = (typeof CUSTOMER_LEAD_SOURCES)[number];

export const RETAILER_LEAD_SOURCES = [
  'CUSTOMER_SITE_CTA',
  'PARTNER_PAGE',
  'REFERRAL',
  'OUTBOUND',
  'EVENT',
  'ADMIN',
  'OTHER',
] as const;
export type RetailerLeadSource = (typeof RETAILER_LEAD_SOURCES)[number];

export const LEAD_CONSENT_TYPES = [
  'EXPLICIT_CHECKBOX',
  'DOUBLE_OPT_IN',
  'IMPLIED_TRANSACTIONAL',
] as const;
export type LeadConsentType = (typeof LEAD_CONSENT_TYPES)[number];

export const MEMBERSHIP_INTEREST_KINDS = ['WAITLIST', 'NOTIFY_ME', 'LEARN_MORE'] as const;
export type MembershipInterestKind = (typeof MEMBERSHIP_INTEREST_KINDS)[number];

export const MEMBERSHIP_MOTIVATIONS = [
  'FREE_ELIGIBLE_DELIVERY',
  'LATE_NIGHT_BENEFITS',
  'EXCLUSIVE_OFFERS',
  'MEMBER_SAVINGS',
  'OTHER',
] as const;
export type MembershipMotivation = (typeof MEMBERSHIP_MOTIVATIONS)[number];

export const LEAD_EVENT_TYPES = [
  'CREATED',
  'UPDATED',
  'STATUS_CHANGED',
  'CONTACTED',
  'QUALIFIED',
  'CONVERTED',
  'CONSENT_GRANTED',
  'CONSENT_WITHDRAWN',
  'EXPORTED',
  'CLOSED',
  'NOTE_ADDED',
] as const;
export type LeadEventType = (typeof LEAD_EVENT_TYPES)[number];

// -- Attribution and analytics ---------------------------------------------

export const ATTRIBUTION_TOUCH_KINDS = ['FIRST', 'INTERMEDIATE', 'LAST'] as const;
export type AttributionTouchKind = (typeof ATTRIBUTION_TOUCH_KINDS)[number];

/** The full first-party attribution field set captured wherever available. */
export const ATTRIBUTION_FIELDS = [
  'utmSource',
  'utmMedium',
  'utmCampaign',
  'utmContent',
  'utmTerm',
  'referrer',
  'landingPage',
  'signupSource',
  'leadSource',
] as const;
export type AttributionField = (typeof ATTRIBUTION_FIELDS)[number];

export const ANALYTICS_EVENT_CATEGORIES = [
  'DISCOVERY',
  'SEARCH',
  'PRODUCT',
  'STORE',
  'CART',
  'ACCOUNT',
  'LEAD',
  'MEMBERSHIP',
  'AFTER_HOURS',
  'FUNNEL',
] as const;
export type AnalyticsEventCategory = (typeof ANALYTICS_EVENT_CATEGORIES)[number];

export const ANALYTICS_SUBJECT_KINDS = [
  'PRODUCT',
  'VARIANT',
  'LISTING',
  'STORE',
  'RETAILER',
  'CATEGORY',
  'BRAND',
  'CART',
  'LEAD',
  'MARKET',
  'SEARCH',
  'MEMBERSHIP',
] as const;
export type AnalyticsSubjectKind = (typeof ANALYTICS_SUBJECT_KINDS)[number];

export const ANALYTICS_SURFACES = ['WEB', 'MOBILE_WEB', 'MOBILE_APP', 'ADMIN', 'SYSTEM'] as const;
export type AnalyticsSurface = (typeof ANALYTICS_SURFACES)[number];

export const ANALYTICS_ADAPTER_KINDS = ['FIRST_PARTY', 'MOCK', 'SANDBOX', 'LIVE'] as const;
export type AnalyticsAdapterKind = (typeof ANALYTICS_ADAPTER_KINDS)[number];

/**
 * The approved conversion funnel, in order. CHECKOUT and PURCHASE are declared
 * here and owned by Phase 4: the funnel is complete as a definition while being
 * honest that two steps cannot yet fire.
 */
export const FUNNEL_STEPS = [
  'VISITOR',
  'LOCATION',
  'BROWSE',
  'PRODUCT_VIEW',
  'ADD_TO_BAG',
  'ACCOUNT_OR_LEAD',
  'CHECKOUT',
  'PURCHASE',
  'REPEAT',
] as const;
export type FunnelStep = (typeof FUNNEL_STEPS)[number];

/** Which phase implements each step. Used to render an honest funnel report. */
export const FUNNEL_STEP_OWNER_PHASE: Readonly<Record<FunnelStep, number>> = {
  VISITOR: 3,
  LOCATION: 3,
  BROWSE: 3,
  PRODUCT_VIEW: 3,
  ADD_TO_BAG: 3,
  ACCOUNT_OR_LEAD: 3,
  CHECKOUT: 4,
  PURCHASE: 4,
  REPEAT: 8,
};

// -- Discovery configuration ------------------------------------------------

export const HOMEPAGE_SECTION_KINDS = [
  'HERO',
  'AFTER_HOURS',
  'CATEGORY_GRID',
  'NEARBY_STORES',
  'TRENDING_PRODUCTS',
  'BEAUTY_BURST_DIRECT',
  'MEMBERSHIP',
  'RECENTLY_VIEWED',
  'RETAILER_CTA',
  'LEAD_CAPTURE',
] as const;
export type HomepageSectionKind = (typeof HOMEPAGE_SECTION_KINDS)[number];

export const STORE_POLICY_KINDS = [
  'RETURNS',
  'EXCHANGES',
  'PICKUP',
  'DELIVERY',
  'HYGIENE',
  'SPECIAL_ORDER',
  'OTHER',
] as const;
export type StorePolicyKind = (typeof STORE_POLICY_KINDS)[number];

// -- Search -----------------------------------------------------------------

export const SEARCH_ENTITY_KINDS = ['PRODUCT', 'BRAND', 'CATEGORY', 'VARIANT', 'STORE'] as const;
export type SearchEntityKind = (typeof SEARCH_ENTITY_KINDS)[number];

export const SEARCH_SORT_OPTIONS = [
  'RELEVANCE',
  'PRICE_ASC',
  'PRICE_DESC',
  'DISTANCE',
  'DELIVERY_ESTIMATE',
  'POPULARITY',
  'NEWEST',
] as const;
export type SearchSortOption = (typeof SEARCH_SORT_OPTIONS)[number];

/**
 * Filters offered on the customer search surface. Availability filters map onto
 * the composer rather than onto a raw column, so an "Overnight" filter can never
 * return a listing whose eligibility has not actually been resolved.
 */
export const SEARCH_FILTER_KEYS = [
  'brand',
  'category',
  'color',
  'shade',
  'length',
  'texture',
  'size',
  'style',
  'priceMinCents',
  'priceMaxCents',
  'maxDistanceMiles',
  'openNow',
  'openLate',
  'overnight',
  'pickup',
  'delivery',
  'membershipEligible',
  'sellerId',
  'beautyBurstDirect',
  'inStockOnly',
  'onSaleOnly',
] as const;
export type SearchFilterKey = (typeof SEARCH_FILTER_KEYS)[number];
